'use client';

import React from 'react';
import Link from 'next/link';
import { ExternalLink } from 'lucide-react';
import { track } from '@/utils/analytics';
import type { NavLink } from '@/lib/site/nav';
import TrackedLink from './TrackedLink';

/** Where a nav link sits; becomes the `source` prop of the link's event. */
export type NavSource = 'menu' | 'mobile-menu' | 'footer';

/**
 * One link from the lib/site/nav model. External links open a new tab with an icon and a screen-reader note;
 * links with an `event` fire it once (the model's own props win, so About keeps source 'resources'/'footer').
 */
export default function NavItemLink({ link, source, className }: { link: NavLink; source: NavSource; className?: string }) {
  const props = link.event ? { source, ...link.event.props } : undefined;

  if (link.external) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onClick={link.event ? () => track(link.event!.name, props) : undefined}
      >
        {link.label}
        <ExternalLink aria-hidden="true" className="ml-1 inline-block h-[13px] w-[13px] align-[-1px] text-ink-muted" />
        <span className="sr-only"> (opens a new site)</span>
      </a>
    );
  }
  if (link.event) {
    return <TrackedLink href={link.href} event={link.event.name} props={props} className={className}>{link.label}</TrackedLink>;
  }
  return <Link href={link.href} className={className}>{link.label}</Link>;
}
